import { atomWithStorage, createJSONStorage } from 'jotai/utils';

import type { AlertDismissals } from '@cfa/pathway-fe-shared/alerts';

import { STORAGE_KEYS } from '@/atoms/storageKeys';

// localStorage is untrusted: keep only `{ [userId]: string[] }` entries so a
// corrupted value can't break filtering or recording dismissals.
const toDismissals = (value: unknown): AlertDismissals => {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return {};
  }
  return Object.fromEntries(
    Object.entries(value).flatMap(([userId, ids]) =>
      Array.isArray(ids)
        ? [[userId, ids.filter((id): id is string => typeof id === 'string')]]
        : [],
    ),
  );
};

const jsonStorage = createJSONStorage<AlertDismissals>(() => localStorage);

const dismissalsStorage: typeof jsonStorage = {
  ...jsonStorage,
  getItem: (key, initialValue) =>
    toDismissals(jsonStorage.getItem(key, initialValue)),
  // Other tabs' storage events bypass `getItem`, so validate them too.
  subscribe: (key, callback, initialValue) =>
    jsonStorage.subscribe?.(
      key,
      value => {
        callback(toDismissals(value));
      },
      initialValue,
    ),
};

/** userId → LaunchDarkly message-block ids that user dismissed on this device. */
export const alertMessageBlockDismissalsAtom = atomWithStorage<AlertDismissals>(
  STORAGE_KEYS.ALERT_MESSAGE_BLOCK_DISMISSALS,
  {},
  dismissalsStorage,
  { getOnInit: true },
);
